/**
 * PDF-export style normalisation for html2canvas 1.x.
 *
 * Root cause (production): Tailwind v4 emits unsupported colour syntax that html2canvas
 * cannot parse (only rgb/rgba/hsl/hsla are supported). The proposal cover gradient
 * compiles to:
 *   .bg-gradient-to-t { --tw-gradient-position: to top in oklab; ... }
 *   .from-[#0B1220]/75 { --tw-gradient-from: oklab(...); }
 * which throws: Attempting to parse an unsupported color function "oklab".
 *
 * This module runs only on the html2canvas clone (and optional pre-scan diagnostics).
 * Link B / live proposal DOM is never mutated.
 */

export const PROPOSAL_PDF_EXPORT_SAFE_CLASS = "proposal-pdf-export-safe";

const UNSUPPORTED_COLOR_TOKEN_RE =
  /oklch\(|oklab\(|color-mix\(|(?:^|[\s,(])lab\(|(?:^|[\s,(])lch\(|(?:^|[\s,(])color\(|\bin\s+oklab\b|\bin\s+oklch\b/i;

/** Replace unsupported colour functions; order matters (oklab before lab, etc.). */
export function replaceUnsupportedPdfColorFunctions(css: string): string {
  return css
    .replace(/oklch\([^)]*\)/gi, "#1A2744")
    .replace(/oklab\([^)]*\)/gi, "#1A2744")
    .replace(/color-mix\([^)]*\)/gi, "#1A2744")
    .replace(/(^|[\s,(])lab\([^)]*\)/gi, "$1#1A2744")
    .replace(/(^|[\s,(])lch\([^)]*\)/gi, "$1#1A2744")
    .replace(/(^|[\s,(])color\([^)]*\)/gi, "$1#1A2744")
    .replace(/to\s+(top|bottom|left|right)(?:\s+(?:top|bottom|left|right))?\s+in\s+oklab/gi, "to $1")
    .replace(/\bin\s+oklab\b/gi, "")
    .replace(/\bin\s+oklch\b/gi, "")
    .replace(/\bin\s+lab\b/gi, "");
}

/** Proposal brand tokens — hex only, never oklch/oklab. */
/** Quotation cream — matches cover / PROPOSAL_BG (never oklch). */
const PDF_SAFE_PAGE_BG = "#FAFAF7";

export const PROPOSAL_PDF_SAFE_CSS_VARS: Record<string, string> = {
  "--background": PDF_SAFE_PAGE_BG,
  "--foreground": "#1A2744",
  "--card": PDF_SAFE_PAGE_BG,
  "--card-foreground": "#1A2744",
  "--popover": PDF_SAFE_PAGE_BG,
  "--popover-foreground": "#1A2744",
  "--primary": "#C8A96A",
  "--primary-foreground": "#1A2744",
  "--secondary": "#F3F4F6",
  "--secondary-foreground": "#1A2744",
  "--muted": "#F3F4F6",
  "--muted-foreground": "#6B7280",
  "--accent": PDF_SAFE_PAGE_BG,
  "--accent-foreground": "#1A2744",
  "--destructive": "#dc2626",
  "--destructive-foreground": "#fafafa",
  "--border": "#e5e7eb",
  "--input": "#e5e7eb",
  "--ring": "#C8A96A",
  "--tw-gradient-from": "rgba(11, 18, 32, 0.75)",
  "--tw-gradient-via": "rgba(11, 18, 32, 0.2)",
  "--tw-gradient-to": "rgba(11, 18, 32, 0)",
  "--tw-gradient-stops":
    "rgba(11, 18, 32, 0.75), rgba(11, 18, 32, 0.2), rgba(11, 18, 32, 0)",
  "--tw-gradient-via-stops":
    "rgba(11, 18, 32, 0.75), rgba(11, 18, 32, 0.2), rgba(11, 18, 32, 0)",
  "--tw-gradient-position": "to top",
};

const COLOR_PROPS = [
  "color",
  "background-color",
  "border-color",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "outline-color",
  "text-decoration-color",
  "caret-color",
  "fill",
  "stroke",
  "column-rule-color",
] as const;

const SHADOW_PROPS = ["box-shadow", "text-shadow"] as const;

const RESET_PROPS: Array<[string, string]> = [
  ["backdrop-filter", "none"],
  ["-webkit-backdrop-filter", "none"],
  ["filter", "none"],
  ["mix-blend-mode", "normal"],
  ["isolation", "auto"],
  ["mask-image", "none"],
  ["-webkit-mask-image", "none"],
  ["clip-path", "none"],
  ["content-visibility", "visible"],
];

const EXPORT_SAFE_INLINE_CSS = `
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS},
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} * {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  mix-blend-mode: normal !important;
  content-visibility: visible !important;
}
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} {
  background-color: ${PDF_SAFE_PAGE_BG} !important;
  color: #1A2744 !important;
  --tw-gradient-position: to top !important;
  --tw-gradient-from: rgba(11, 18, 32, 0.75) !important;
  --tw-gradient-via: rgba(11, 18, 32, 0.2) !important;
  --tw-gradient-to: rgba(11, 18, 32, 0) !important;
}
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .bg-gradient-to-t,
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .bg-gradient-to-b,
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .bg-gradient-to-r,
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .bg-gradient-to-l {
  --tw-gradient-position: to top !important;
  background-image: linear-gradient(to top, rgba(11, 18, 32, 0.75), rgba(11, 18, 32, 0.2), rgba(11, 18, 32, 0)) !important;
}
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .text-muted-foreground {
  color: #6B7280 !important;
}
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .text-foreground {
  color: #1A2744 !important;
}
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .bg-background {
  background-color: ${PDF_SAFE_PAGE_BG} !important;
}
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .bg-muted {
  background-color: #F3F4F6 !important;
}
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .border,
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} .border-border {
  border-color: #e5e7eb !important;
}
.${PROPOSAL_PDF_EXPORT_SAFE_CLASS} svg {
  color: inherit;
}
`;

export function cssContainsUnsupportedColorSyntax(css: string): boolean {
  return UNSUPPORTED_COLOR_TOKEN_RE.test(css);
}

function isRgbOrHexOrNamedSafe(value: string): boolean {
  const v = value.trim();
  if (!v || v === "transparent" || v === "none" || v === "currentcolor" || v === "currentColor") {
    return true;
  }
  if (/^rgba?\(/i.test(v)) return true;
  if (/^hsla?\(/i.test(v)) return true;
  if (/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) return true;
  if (/^[a-z]+$/i.test(v)) return true; // named CSS colours
  return !UNSUPPORTED_COLOR_TOKEN_RE.test(v);
}

let conversionCanvas: HTMLCanvasElement | null = null;

/** Convert any browser-supported colour to rgb/rgba via canvas (html2canvas-safe). */
export function convertCssColorToRgb(value: string, fallback = "#1A2744"): string {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return fallback;
  if (trimmed === "transparent") return "rgba(0, 0, 0, 0)";
  if (isRgbOrHexOrNamedSafe(trimmed) && !UNSUPPORTED_COLOR_TOKEN_RE.test(trimmed)) {
    return trimmed;
  }
  try {
    if (typeof document === "undefined") return fallback;
    if (!conversionCanvas) conversionCanvas = document.createElement("canvas");
    const ctx = conversionCanvas.getContext("2d");
    if (!ctx) return fallback;
    ctx.fillStyle = "#000000";
    ctx.fillStyle = trimmed;
    const normalised = ctx.fillStyle;
    return normalised || fallback;
  } catch {
    return fallback;
  }
}

function applySafeCssVars(target: HTMLElement): void {
  for (const [name, value] of Object.entries(PROPOSAL_PDF_SAFE_CSS_VARS)) {
    target.style.setProperty(name, value);
  }
}

function sanitizeStylesheets(clonedDoc: Document): number {
  let patched = 0;
  clonedDoc.querySelectorAll("style").forEach((node) => {
    const text = node.textContent;
    if (!text || !cssContainsUnsupportedColorSyntax(text)) return;
    node.textContent = replaceUnsupportedPdfColorFunctions(text);
    patched += 1;
  });
  return patched;
}

function injectExportSafeStylesheet(clonedDoc: Document): void {
  const style = clonedDoc.createElement("style");
  style.setAttribute("data-proposal-pdf-export", "true");
  style.textContent = EXPORT_SAFE_INLINE_CSS;
  (clonedDoc.head ?? clonedDoc.documentElement).appendChild(style);
}

function sanitiseBackgroundImage(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "none") return trimmed || "none";
  if (!cssContainsUnsupportedColorSyntax(trimmed) && !/\bin\s+oklab\b/i.test(trimmed)) {
    return trimmed;
  }
  // Cover-style dark wash fallback when gradient cannot be preserved safely.
  if (/gradient/i.test(trimmed)) {
    return "linear-gradient(to top, rgba(11, 18, 32, 0.75), rgba(11, 18, 32, 0.2), rgba(11, 18, 32, 0))";
  }
  return "none";
}

function sanitiseShadow(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "none") return "none";
  if (!cssContainsUnsupportedColorSyntax(trimmed)) return trimmed;
  return "none";
}

function normaliseElementPaintStyles(el: HTMLElement | SVGElement, win: Window): void {
  const computed = win.getComputedStyle(el);

  for (const prop of COLOR_PROPS) {
    const value = computed.getPropertyValue(prop);
    if (!value) continue;
    if (!isRgbOrHexOrNamedSafe(value) || UNSUPPORTED_COLOR_TOKEN_RE.test(value)) {
      el.style.setProperty(prop, convertCssColorToRgb(value));
    }
  }

  const bgImage =
    computed.getPropertyValue("background-image") ||
    (el instanceof HTMLElement ? el.style.backgroundImage : "") ||
    "";
  if (bgImage) {
    const safe = sanitiseBackgroundImage(bgImage);
    if (safe != null) el.style.setProperty("background-image", safe);
  }

  for (const prop of SHADOW_PROPS) {
    const value =
      computed.getPropertyValue(prop) || (el instanceof HTMLElement ? el.style.getPropertyValue(prop) : "") || "";
    if (!value) continue;
    el.style.setProperty(prop, sanitiseShadow(value));
  }

  for (const [prop, value] of RESET_PROPS) {
    const current = computed.getPropertyValue(prop);
    if (current && current !== "none" && current !== "normal" && current !== "auto" && current !== "visible") {
      el.style.setProperty(prop, value);
    }
  }

  // Neutralise Tailwind v4 gradient custom properties that embed oklab.
  const gradientVars = [
    "--tw-gradient-from",
    "--tw-gradient-via",
    "--tw-gradient-to",
    "--tw-gradient-stops",
    "--tw-gradient-via-stops",
    "--tw-gradient-position",
  ] as const;
  for (const name of gradientVars) {
    const raw =
      computed.getPropertyValue(name) ||
      (el instanceof HTMLElement || el instanceof SVGElement ? el.style.getPropertyValue(name) : "") ||
      "";
    if (!raw && name !== "--tw-gradient-position") continue;
    if (!raw || cssContainsUnsupportedColorSyntax(raw) || /\bin\s+oklab\b/i.test(raw)) {
      el.style.setProperty(name, PROPOSAL_PDF_SAFE_CSS_VARS[name] ?? "to top");
    }
  }
}

function normaliseInlinePaintFallbacks(el: HTMLElement | SVGElement): void {
  const inlineBg = el.style?.backgroundImage;
  if (inlineBg && (cssContainsUnsupportedColorSyntax(inlineBg) || /\bin\s+oklab\b/i.test(inlineBg))) {
    el.style.setProperty("background-image", sanitiseBackgroundImage(inlineBg) ?? "none");
  }
  for (const prop of SHADOW_PROPS) {
    const inlineShadow = el.style?.getPropertyValue(prop);
    if (inlineShadow && cssContainsUnsupportedColorSyntax(inlineShadow)) {
      el.style.setProperty(prop, sanitiseShadow(inlineShadow));
    }
  }
  for (const name of [
    "--tw-gradient-from",
    "--tw-gradient-via",
    "--tw-gradient-to",
    "--tw-gradient-stops",
    "--tw-gradient-via-stops",
    "--tw-gradient-position",
  ] as const) {
    const raw = el.style?.getPropertyValue(name);
    if (!raw) continue;
    if (cssContainsUnsupportedColorSyntax(raw) || /\bin\s+oklab\b/i.test(raw)) {
      el.style.setProperty(name, PROPOSAL_PDF_SAFE_CSS_VARS[name] ?? "to top");
    }
  }
}

function normaliseSubtree(root: HTMLElement, win: Window | null): void {
  const nodes: Array<HTMLElement | SVGElement> = [root];
  root.querySelectorAll("*").forEach((node) => {
    if (node instanceof HTMLElement || node instanceof SVGElement) nodes.push(node);
  });
  for (const node of nodes) {
    normaliseInlinePaintFallbacks(node);
    if (win) normaliseElementPaintStyles(node, win);
  }
}

export type ProposalPdfUnsupportedStyleHit = {
  selector: string;
  property: string;
  value: string;
};

/** Dev/diagnostic scan of a live or cloned subtree for html2canvas-incompatible colours. */
export function findUnsupportedProposalPdfStyles(
  root: HTMLElement,
  win: Window = window,
): ProposalPdfUnsupportedStyleHit[] {
  const hits: ProposalPdfUnsupportedStyleHit[] = [];
  const nodes: Element[] = [root, ...root.querySelectorAll("*")];
  for (const node of nodes) {
    if (!(node instanceof HTMLElement) && !(node instanceof SVGElement)) continue;
    const el = node as HTMLElement | SVGElement;
    const computed = win.getComputedStyle(el);
    const selector =
      el.getAttribute("data-testid") ||
      el.className?.toString?.()?.slice?.(0, 80) ||
      el.tagName.toLowerCase();

    for (const prop of [...COLOR_PROPS, "background-image", ...SHADOW_PROPS]) {
      const value = computed.getPropertyValue(prop);
      if (!value) continue;
      if (cssContainsUnsupportedColorSyntax(value) || /\bin\s+oklab\b/i.test(value)) {
        hits.push({ selector: String(selector), property: prop, value: value.slice(0, 240) });
      }
    }

    for (const name of ["--tw-gradient-from", "--tw-gradient-via", "--tw-gradient-to", "--tw-gradient-position"]) {
      const value = computed.getPropertyValue(name);
      if (!value) continue;
      if (cssContainsUnsupportedColorSyntax(value) || /\bin\s+oklab\b/i.test(value)) {
        hits.push({ selector: String(selector), property: name, value: value.slice(0, 240) });
      }
    }
  }
  return hits;
}

/**
 * Prepare the html2canvas clone so proposal PDF pages never expose unsupported colour syntax.
 * Does not mutate the live Link B / proposal DOM.
 */
export function prepareProposalPdfExportClone(clonedDoc: Document, clonedRoot: HTMLElement): void {
  sanitizeStylesheets(clonedDoc);

  const html = clonedDoc.documentElement;
  if (html instanceof HTMLElement) applySafeCssVars(html);
  if (clonedDoc.body instanceof HTMLElement) applySafeCssVars(clonedDoc.body);

  clonedRoot.classList.add(PROPOSAL_PDF_EXPORT_SAFE_CLASS);
  applySafeCssVars(clonedRoot);
  injectExportSafeStylesheet(clonedDoc);

  const win = clonedDoc.defaultView ?? (typeof window !== "undefined" ? window : null);
  normaliseSubtree(clonedRoot, win);
}

/**
 * Optional pre-capture pass on the off-screen export package (already detached visually).
 * Safe to call on the temporary export root; still prefer clone normalisation as primary.
 */
export function normaliseProposalPdfExportStyles(root: HTMLElement): void {
  applySafeCssVars(root);
  root.classList.add(PROPOSAL_PDF_EXPORT_SAFE_CLASS);
  normaliseSubtree(root, typeof window !== "undefined" ? window : null);
}
